/**
 * Timezone-safe flight durations.
 *
 * WHY NOT `last.arrival_date - first.departure_date`
 * --------------------------------------------------
 * Segment timestamps are the wall-clock time AT EACH AIRPORT encoded as though
 * it were UTC (see `wallClockAtToUnixSeconds` in amadeus-flight.service.ts). The
 * difference between two of them is a real elapsed time ONLY when both airports
 * share a timezone. A TLV 06:00 → IST → JFK 14:00 itinerary subtracts to 8h; the
 * traveller is actually in the air and in transit for 16h. That subtraction is
 * what the flight cards used to print as "journey duration".
 *
 * WHAT IS SAFE
 * ------------
 *  - The supplier's own durations. The backend now stamps them on every segment
 *    (`duration_minutes`, `journey_duration_minutes` — see
 *    sky360-ms libs/core/src/utils/flight-duration.ts), and the raw `duration`
 *    string has always been there (ISO "PT5H30M" from Amadeus, minutes from
 *    Cockpit, "HH:MM" from ALP).
 *  - Ground time at a connection: `next.departure - previous.arrival` subtracts
 *    two wall-clock times at the SAME airport, so it is a real elapsed time.
 *  - Calendar-day offsets ("+1"): comparing the two wall-clock DATES is exactly
 *    what the traveller sees on the boarding pass.
 *
 * When none of the safe sources is available the leg duration is reported as
 * unknown (null) rather than as a wall-clock guess — a missing duration on a
 * card is better than a wrong one.
 */

const SECONDS_PER_DAY = 86400;

const ISO_DURATION =
  /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i;
const CLOCK_DURATION = /^(\d{1,3}):(\d{2})(?::\d{2})?$/;
const PLAIN_NUMBER = /^\d+(?:\.\d+)?$/;

/**
 * Minutes in a provider duration value, or null when it cannot be read.
 * MIRROR of sky360-ms `parseDurationToMinutes` — keep the two in step.
 */
export const parseDurationToMinutes = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? Math.round(value) : null;
  }
  if (typeof value !== 'string') return null;

  const raw = value.trim();
  if (!raw) return null;

  if (PLAIN_NUMBER.test(raw)) {
    const minutes = Math.round(Number(raw));
    return minutes > 0 ? minutes : null;
  }

  const clock = CLOCK_DURATION.exec(raw);
  if (clock) {
    const minutes = Number(clock[1]) * 60 + Number(clock[2]);
    return minutes > 0 ? minutes : null;
  }

  const iso = ISO_DURATION.exec(raw);
  if (iso && (iso[1] || iso[2] || iso[3] || iso[4])) {
    const minutes =
      Number(iso[1] || 0) * 24 * 60 +
      Number(iso[2] || 0) * 60 +
      Number(iso[3] || 0) +
      Math.round(Number(iso[4] || 0) / 60);
    return minutes > 0 ? minutes : null;
  }

  return null;
};

export interface DurationSegment {
  departure_date?: number | string | null;
  arrival_date?: number | string | null;
  duration?: unknown;
  duration_minutes?: number | null;
  journey_duration_minutes?: number | null;
}

const positiveMinutes = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : null;

const toSeconds = (value: unknown): number | null => {
  const n = Number(value);
  return value !== null && value !== '' && Number.isFinite(n) && n > 0
    ? n
    : null;
};

/** Flying time of ONE segment, in minutes, or null when unknown. */
export const getSegmentFlyingMinutes = (
  segment?: DurationSegment | null
): number | null => {
  if (!segment) return null;
  // The backend sends `duration_minutes: null` on purpose for a segment whose
  // own flying time is unknown (an ALP connection, whose `duration` string is
  // the WHOLE flight) — so an explicit null must not fall back to `duration`.
  if ('duration_minutes' in segment) {
    return positiveMinutes(segment.duration_minutes);
  }
  return parseDurationToMinutes(segment.duration);
};

/**
 * Minutes on the ground between two consecutive segments of one leg. Both
 * timestamps are wall-clock at the connecting airport, so this is elapsed time.
 * Null when either is missing or the gap is negative (bad data).
 */
export const getLayoverMinutes = (
  landing?: DurationSegment | null,
  boarding?: DurationSegment | null
): number | null => {
  const landed = toSeconds(landing?.arrival_date);
  const departs = toSeconds(boarding?.departure_date);
  if (landed === null || departs === null || departs < landed) return null;
  return Math.round((departs - landed) / 60);
};

/**
 * Elapsed minutes from the first take-off to the last landing of a leg —
 * connections included — or null when it cannot be known exactly.
 *
 * 1. `journey_duration_minutes` (the supplier's own leg total).
 * 2. Every segment's flying time + the ground time at every connection.
 */
export const getLegDurationMinutes = (
  segments?: DurationSegment[] | DurationSegment | null
): number | null => {
  const list = Array.isArray(segments) ? segments : segments ? [segments] : [];
  if (list.length === 0) return null;

  const supplierTotal = positiveMinutes(list[0]?.journey_duration_minutes);
  if (supplierTotal !== null) return supplierTotal;

  let total = 0;
  for (let i = 0; i < list.length; i++) {
    const flying = getSegmentFlyingMinutes(list[i]);
    if (flying === null) return null;
    total += flying;
    if (i < list.length - 1) {
      const ground = getLayoverMinutes(list[i], list[i + 1]);
      if (ground === null) return null;
      total += ground;
    }
  }
  return total;
};

/**
 * How many calendar days after departure the arrival lands, by the LOCAL dates
 * at each airport ("+1" on a red-eye). Never derived from the elapsed time: a
 * 5h flight leaving at 23:00 lands the next day, a 20h one leaving at 00:30 may
 * not.
 */
export const getDayOffset = (
  departureAt?: number | string | null,
  arrivalAt?: number | string | null
): number => {
  const dep = toSeconds(departureAt);
  const arr = toSeconds(arrivalAt);
  if (dep === null || arr === null) return 0;
  return Math.floor(arr / SECONDS_PER_DAY) - Math.floor(dep / SECONDS_PER_DAY);
};

/** `{ hours, minutes }` of a minute count, for "5h 30m"-style labels. */
export const splitMinutes = (
  total: number
): { hours: number; minutes: number } => ({
  hours: Math.floor(total / 60),
  minutes: total % 60,
});

/**
 * "5h 30m" with the tenant's translated units. A zero minute part is dropped
 * ("3h"), a sub-hour duration shows minutes alone ("45m"). Returns '' when the
 * duration is unknown or a needed unit is untranslated — never a naked number.
 */
export const formatMinutes = (
  total: number | null | undefined,
  hourUnit?: string,
  minuteUnit?: string
): string => {
  if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) {
    return '';
  }
  const { hours, minutes } = splitMinutes(Math.round(total));
  const showHours = hours > 0;
  const showMinutes = minutes > 0 || hours === 0;
  if ((showHours && !hourUnit) || (showMinutes && !minuteUnit)) return '';
  return [
    showHours ? `${hours}${hourUnit}` : null,
    showMinutes ? `${minutes}${minuteUnit}` : null,
  ]
    .filter(Boolean)
    .join(' ');
};
